import React from 'react';
import { useNavigate } from 'react-router-dom';

const About: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white"></div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6">
            We help agents make more money.
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            That's it. No corporate mission statement. No fluff. 
            We built this because we were tired of seeing good agents get bad deals.
          </p>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="text-sm font-semibold text-navy-600 uppercase tracking-wider">The Problem</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-6">
                Most IMOs take too much and give too little.
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  You've seen it. Agents lose a huge cut of their earnings. The "support" is a PDF and a phone number that goes to voicemail. 
                  The leads are recycled. The tech is from 2010.
                </p>
                <p>
                  Agents deserve better. They deserve to keep more of what they earn. 
                  They deserve tools that actually work. They deserve someone who picks up the phone.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-red-400 mt-2.5 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-slate-900">Agents losing 30-50% of their earnings</p>
                    <p className="text-sm text-slate-500 mt-1">Money that should be in your pocket</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-red-400 mt-2.5 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-slate-900">Outdated systems and no real training</p>
                    <p className="text-sm text-slate-500 mt-1">Left to figure it out on your own</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-red-400 mt-2.5 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-slate-900">Buying leads from random vendors</p>
                    <p className="text-sm text-slate-500 mt-1">No control over quality or freshness</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-navy-600 uppercase tracking-wider">What We Do</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
              We fixed the things that were broken.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Higher Payouts",
                description: "You keep more. We take less. It's simple math. Our agents consistently out-earn their peers at other IMOs."
              },
              {
                title: "Leads In-House",
                description: "Mortgage protection, final expense, IUL, aged leads, live transfers. We generate and sell leads directly to our agents. No third parties."
              },
              {
                title: "Actual Support",
                description: "Call us. We answer. Need help with a case? We're there. No tickets. No waiting 3 days for an email."
              },
              {
                title: "Modern Tools",
                description: "Dialers that work. A CRM that doesn't crash. Lead tracking that makes sense. Built in this decade."
              },
              {
                title: "Direct Contracts",
                description: "Work directly with 28+ carriers. No middlemen taking a piece of every deal you close."
              },
              {
                title: "No Nonsense",
                description: "No mandatory meetings. No rah-rah calls. Just the resources you need to sell and get paid."
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 border border-slate-200 hover:border-navy-300 hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-lg font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers Section */}
      <section className="py-20 lg:py-28 bg-navy-900">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              The numbers speak for themselves.
            </h2>
            <p className="text-navy-300 mt-4 max-w-xl mx-auto">
              We don't need to oversell it. Here's where we're at.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {[
              { number: "1,000+", label: "Agents", sub: "and growing" },
              { number: "28+", label: "Carriers", sub: "direct contracts" },
              { number: "24/7", label: "Support", sub: "real people" },
              { number: "6+", label: "Years", sub: "in business" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">{stat.number}</div>
                <div className="text-white font-medium">{stat.label}</div>
                <div className="text-navy-400 text-sm mt-1">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We're For Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-sm font-semibold text-navy-600 uppercase tracking-wider">Who This Is For</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
              We're not for everyone. And that's okay.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Good Fit */}
            <div className="bg-emerald-50 rounded-2xl p-8 border border-emerald-100">
              <h3 className="text-lg font-bold text-emerald-800 mb-6">Good fit</h3>
              <ul className="space-y-4">
                {[
                  "Licensed or unlicensed agents ready to work",
                  "People who want to build something real",
                  "Agents tired of getting shortchanged",
                  "Those who value results over hype"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Not a Good Fit */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-500 mb-6">Not a good fit</h3>
              <ul className="space-y-4">
                {[
                  "Looking for get-rich-quick schemes",
                  "Not willing to put in the work",
                  "Expecting us to do the selling for you",
                  "Just shopping for the lowest price"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-500">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-400 flex-shrink-0"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-sm font-semibold text-navy-600 uppercase tracking-wider">How It Works</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
              Getting started takes about 10 minutes.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Apply", description: "Fill out a quick application. We review it within 24 hours. No 47-page forms." },
              { step: "02", title: "Get Set Up", description: "We handle your contracting with carriers. You get access to our tools and training." },
              { step: "03", title: "Start Selling", description: "Order leads, use the dialer, close deals. Keep more of what you earn." }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-navy-100 text-navy-700 font-bold text-lg mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Ready to stop leaving money on the table?
          </h2>
          <p className="text-xl text-slate-600 mb-10">
            Join the agents who decided they deserved better.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => {
                navigate('/apply');
                window.scrollTo(0, 0);
              }}
              className="w-full sm:w-auto px-8 py-4 bg-navy-700 text-white font-semibold rounded-xl hover:bg-navy-800 transition-colors shadow-lg hover:shadow-xl"
            >
              Apply Now
            </button>
            <button
              onClick={() => {
                navigate('/contact');
                window.scrollTo(0, 0);
              }}
              className="w-full sm:w-auto px-8 py-4 bg-white text-slate-700 font-semibold rounded-xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
            >
              Talk to Us First
            </button>
          </div>

          <p className="text-sm text-slate-500 mt-8">
            Questions? Call <a href="tel:+14242446608" className="text-navy-600 font-medium hover:underline">(424) 244-6608</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
